import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Box, Container, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import { useGetBundle } from '~/hooks/apis/cruises/useGetBundle';
import useAppNavigation from '~/hooks/useAppNavigation';

import BundleForm from '../components/BundleForm';

import BundlesFilterList from './BundlesFilter/BundlesFilterList';
import BundlesItemList from './BundlesItem/BundlesItemList';

function BundlesEdit() {
  const { bundleId } = useParams<{ bundleId: string }>();
  const { pathname } = useLocation();
  const { replace } = useAppNavigation();

  // Routes are declared with literal `/items` and `/filters` path segments
  // (see App.tsx), so derive the tab from the URL itself rather than useParams.
  const tab: 'items' | 'filters' | undefined = pathname.includes('/filters')
    ? 'filters'
    : pathname.includes('/items')
      ? 'items'
      : undefined;

  const { data: bundle, isLoading, error } = useGetBundle(bundleId);

  useEffect(() => {
    if (!tab && bundleId) {
      replace(`/cruises/bundles/edit/${bundleId}/items`);
    }
  }, [tab, bundleId, replace]);

  if (isLoading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">Error loading bundle: {error.message}</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Helmet>
        <title>Edit Bundle: {bundle?.name}</title>
      </Helmet>

      <PageHeader title={`Edit Bundle: ${bundle?.name || ''}`} backButton="/cruises/bundles" />

      {bundle && (
        <Box mt={1}>
          <Box>
            <BundleForm bundle={bundle} />
          </Box>
          <Box mt={3} border={1} borderColor="divider" borderRadius={1}>
            <Tabs value={tab || false} variant="scrollable">
              <Tab value="items" label="Bundle Items" to={`/cruises/bundles/edit/${bundleId}/items`} component={Link} />
              <Tab
                value="filters"
                label="Bundle Filters"
                to={`/cruises/bundles/edit/${bundleId}/filters`}
                component={Link}
              />
            </Tabs>

            <Box borderTop={1} borderColor="divider">
              {tab === 'items' && <BundlesItemList />}
              {tab === 'filters' && <BundlesFilterList vendorId={bundle.fkVendorId} />}
            </Box>
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default BundlesEdit;
